import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAppContext } from '../contexts/AppContext';
import { useToast } from '../contexts/ToastContext';
import { chatAPI, countriesAPI } from '../services/api';
import ChatStarterChips, { useStarterChips } from './ChatStarterChips';

// sessionStorage flag: dismissed the welcome bubble once already this browser session.
// Deliberately session-scoped (NOT localStorage / per-user-forever) — reappears on a
// fresh tab/session, per product decision. Do not change to a permanent dismissal.
const WELCOME_BUBBLE_KEY = 'viza_chat_widget_welcomed';

// Map the in-memory chat history to the {role, content} shape the AI engine expects (last 20 turns).
// Duplicated from ChatPage.jsx on purpose — this widget is a self-contained, lightweight v1
// (see in_progress/issues.md #5); not worth extracting a shared module for one small helper.
function buildConversationHistory(chatHistory) {
  return (chatHistory || [])
    .filter((m) => !m.isLoading && m.content && (m.sender === 'user' || m.sender === 'bot' || m.sender === 'assistant'))
    .slice(-20)
    .map((m) => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.content }));
}

// Simple heuristic language detection — same logic as ChatPage.jsx (duplicated, see note above).
function detectLanguage(text) {
  const turkishPattern = /[çğışöüÇĞİŞÖÜ]/;
  const turkishWords = /\b(ve|veya|için|ile|bir|bu|şu|o|ben|sen|biz|siz|onlar|mı|mi|mu|mü|ne|nasıl|neden|nerede|kim|hangi|kaç|var|yok|evet|hayır|lütfen|teşekkür|merhaba|günaydın|iyi|kötü|büyük|küçük)\b/i;
  const englishWords = /\b(the|and|or|for|with|a|an|this|that|i|you|we|they|is|are|was|were|what|how|why|where|who|which|when|yes|no|please|thank|hello|good|bad|big|small)\b/i;
  if (turkishPattern.test(text) || turkishWords.test(text)) return 'tr';
  if (englishWords.test(text)) return 'en';
  return 'tr';
}

// Minimal markdown → HTML, same rules as ChatPage.jsx's formatMessage (duplicated, see note above).
function formatMessage(content) {
  return content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/(https?:\/\/[^\s<>"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\n/g, '<br>');
}

// Floating bottom-right chat widget — lightweight v1 (plain text thread + input only), now with
// the same starter chips as the full ChatPage on an empty thread (T4 — see ChatStarterChips.jsx,
// the single shared source both surfaces import from so their chip behavior can't diverge again).
// Still no file upload or inline cover-letter drafting here — the passport-upload shortcut chip
// and "Tam sohbete git" both route to the full /chat page (ChatPage.jsx) for those. Shares
// AppContext's `state.chatHistory` (same reducer actions ChatPage.jsx uses) so the thread is
// identical whichever surface the user is on — no separate conversation-id plumbing needed;
// chatAPI.getHistory/sendMessage already key the conversation by (authenticated user, country)
// server-side.
function ChatWidget() {
  const { state, dispatch } = useAppContext();
  const { error: showError } = useToast();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [showWelcomeBubble, setShowWelcomeBubble] = useState(() => {
    try {
      return sessionStorage.getItem(WELCOME_BUBBLE_KEY) !== '1';
    } catch {
      return false;
    }
  });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [freeChatLimitReached, setFreeChatLimitReached] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [countries, setCountries] = useState([]); // for resolving selectedCountry's display name (starter chips, T4)

  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const hasFetchedHistoryRef = useRef(false);
  const hasFetchedCountriesRef = useRef(false);
  // Pending navigation timer for a server-driven redirect (T4 — mirrors ChatPage.jsx's
  // redirectTimerRef; see that file's handleSendMessage for the full rationale).
  const redirectTimerRef = useRef(null);

  const isAdmin = state.currentUser?.Role === 'admin' || state.currentUser?.Role === 'Admin';

  // Cancel a pending post-redirect-message navigation if the widget unmounts (e.g. the user
  // navigates away by some other means before the timer fires), so an orphaned timer can't
  // yank them somewhere they didn't ask to go.
  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  // Restore the free-chat-limit lock (same localStorage key ChatPage.jsx uses) so a non-paying
  // user can't bypass the paywall simply by using the widget instead of the full page.
  useEffect(() => {
    if (state.selectedCountry && state.currentUser && !state.hasCompletedPayment) {
      const key = `freeChatLimit_${state.currentUser.UserID}_${state.selectedCountry}`;
      setFreeChatLimitReached(localStorage.getItem(key) === 'true');
    } else {
      setFreeChatLimitReached(false);
    }
  }, [state.selectedCountry, state.currentUser, state.hasCompletedPayment]);

  // Load persisted history once, the first time the panel is opened (mirrors ChatPage.jsx's
  // own fetch — harmless if both fire since it's the same server-side conversation).
  useEffect(() => {
    if (!isOpen || hasFetchedHistoryRef.current) return;
    if (!state.selectedCountry || state.chatHistory.length > 0) return;
    hasFetchedHistoryRef.current = true;
    let cancelled = false;
    chatAPI.getHistory(state.selectedCountry)
      .then((msgs) => {
        if (!cancelled && msgs && msgs.length) {
          dispatch({ type: 'SET_CHAT_HISTORY', payload: msgs });
        }
      })
      .catch(() => {
        // Not silent anymore — mirrors ChatPage.jsx's history-restore failure handling
        // so both surfaces behave the same way (visible, non-blocking toast instead of
        // a silently "stuck empty" thread).
        if (!cancelled) {
          showError('Önceki sohbet geçmişiniz yüklenemedi. Sayfayı yenilemeyi deneyebilirsiniz.');
        }
      });
    return () => { cancelled = true; };
  }, [isOpen, state.selectedCountry, state.chatHistory.length, dispatch]);

  // Fetch the active-countries list once, the first time the panel is opened, so starter chip
  // #1 can show the selected country's real Turkish display name (mirrors ChatPage.jsx's own
  // fetch — deliberately NOT a shared cache, see that file's comment for why: AppContext
  // doesn't cache this list anywhere, every page that needs it fetches independently).
  useEffect(() => {
    if (!isOpen || hasFetchedCountriesRef.current) return;
    hasFetchedCountriesRef.current = true;
    countriesAPI.getActiveCountries()
      .then((response) => {
        if (response?.success && response.countries) {
          setCountries(response.countries);
        }
      })
      .catch(() => {
        // Silent on purpose, same as ChatPage.jsx — the chip label just falls back to
        // generic phrasing below if we never resolve a display name.
      });
  }, [isOpen]);

  // Keep the thread scrolled to the latest message whenever it changes or the panel opens.
  useEffect(() => {
    if (!isOpen) return;
    const c = messagesContainerRef.current;
    if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'auto' });
  }, [state.chatHistory, isOpen]);

  // Same resolution ChatPage.jsx uses (real display name, not the slug — see that file's
  // comment for why capitalizing the slug would be wrong).
  const selectedCountryName = useMemo(() => {
    if (!state.selectedCountry || !countries.length) return null;
    const match = countries.find((c) => c.slug === state.selectedCountry);
    return match?.name || null;
  }, [state.selectedCountry, countries]);

  const starterChips = useStarterChips(selectedCountryName);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.overflow = 'hidden';
    el.style.height = '0';
    const sh = el.scrollHeight;
    el.style.height = Math.min(sh, 96) + 'px';
    el.style.overflow = sh > 96 ? 'auto' : 'hidden';
  }, []);

  const markWelcomed = () => {
    try { sessionStorage.setItem(WELCOME_BUBBLE_KEY, '1'); } catch { /* ignore */ }
    setShowWelcomeBubble(false);
  };

  const handleOpen = () => {
    markWelcomed();
    setIsOpen(true);
  };

  const handleGoToFullChat = () => {
    navigate('/chat');
  };

  // `overrideText` (T4): lets a starter-chip click send a fixed string immediately, mirroring
  // ChatPage.jsx's handleSendMessage. Falls back to the live `message` state when omitted.
  const handleSendMessage = async (overrideText) => {
    if (!isAdmin && freeChatLimitReached && !state.hasCompletedPayment) {
      showError('Ücretsiz mesaj hakkınız doldu. Devam etmek için lütfen paket satın alın.');
      return;
    }

    const rawText = typeof overrideText === 'string' ? overrideText : message;
    if (!rawText.trim() || isLoading || !state.selectedCountry) return;

    const userMessage = rawText.trim();
    const detectedLanguage = detectLanguage(userMessage);

    setMessage('');
    requestAnimationFrame(resizeTextarea);

    dispatch({
      type: 'ADD_CHAT_MESSAGE',
      payload: { content: userMessage, sender: 'user', timestamp: new Date() },
    });

    setIsLoading(true);

    const streamingMessageId = Date.now();
    dispatch({
      type: 'ADD_CHAT_MESSAGE',
      payload: { content: '', sender: 'bot', timestamp: new Date(), id: streamingMessageId, isStreaming: true },
    });

    let streamedContent = '';
    let rafPending = false;
    const onChunk = (chunk) => {
      streamedContent += chunk;
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(() => {
          dispatch({ type: 'UPDATE_CHAT_MESSAGE', payload: { id: streamingMessageId, content: streamedContent } });
          rafPending = false;
        });
      }
    };

    try {
      const response = await chatAPI.sendMessage(
        userMessage,
        conversationId,
        detectedLanguage,
        state.selectedCountry,
        onChunk,
        buildConversationHistory(state.chatHistory)
      );

      dispatch({
        type: 'UPDATE_CHAT_MESSAGE',
        payload: { id: streamingMessageId, content: response.response, isStreaming: false },
      });

      setConversationId(response.conversationId);

      // T4: same redirect contract as ChatPage.jsx (see that file's handleSendMessage for the
      // full rationale) — the announcement is already in the thread exactly as streamed above
      // AND already persisted server-side, so no extra client-only message is dispatched here.
      // Also closes the panel so the user lands on the target page instead of a stale-looking
      // floating widget.
      if (response.redirect) {
        if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = setTimeout(() => {
          redirectTimerRef.current = null;
          setIsOpen(false);
          navigate(response.redirect);
        }, 1200);
      }

      if (!isAdmin && !state.hasCompletedPayment) {
        setFreeChatLimitReached(true);
        if (state.selectedCountry && state.currentUser) {
          localStorage.setItem(`freeChatLimit_${state.currentUser.UserID}_${state.selectedCountry}`, 'true');
        }
      }
    } catch (error) {
      console.error('ChatWidget send error:', error);

      let errorMessage = 'Bir hata oluştu, lütfen tekrar deneyin.';
      if (error.code === 'FREE_CHAT_LIMIT_REACHED') {
        setFreeChatLimitReached(true);
        if (state.selectedCountry && state.currentUser) {
          localStorage.setItem(`freeChatLimit_${state.currentUser.UserID}_${state.selectedCountry}`, 'true');
        }
        errorMessage = error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch({
        type: 'UPDATE_CHAT_MESSAGE',
        payload: { id: streamingMessageId, content: errorMessage, isStreaming: false },
      });
    }

    setIsLoading(false);
  };

  const inputDisabled = isLoading || !state.selectedCountry || (!isAdmin && freeChatLimitReached && !state.hasCompletedPayment);

  return (
    <>
      {/* Collapsed: floating action button + optional one-time welcome bubble */}
      {!isOpen && (
        <>
          {showWelcomeBubble && (
            <div className="fixed bottom-24 right-5 z-30 w-72 max-w-[85vw] rounded-2xl border border-line-gray bg-white shadow-xl p-4">
              <button
                type="button"
                onClick={markWelcomed}
                aria-label="Kapat"
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-off-white text-neutral-gray bg-transparent border-none cursor-pointer"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
              </button>
              <button
                type="button"
                onClick={handleOpen}
                className="w-full text-left bg-transparent border-none cursor-pointer p-0"
              >
                <p className="text-sm font-semibold text-viza-black pr-4">Merhaba! 👋</p>
                <p className="mt-1 text-sm text-ink pr-4">Vize sürecine hemen başlayalım mı?</p>
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={handleOpen}
            aria-label="Sohbeti aç"
            className="fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-pear text-viza-black shadow-lg hover:brightness-95 transition-all border-none cursor-pointer"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>forum</span>
          </button>
        </>
      )}

      {/* Expanded panel */}
      {isOpen && (
        <div className="fixed bottom-5 right-5 z-30 flex flex-col w-[380px] max-w-[92vw] h-[560px] max-h-[80vh] rounded-2xl border border-line-gray bg-white shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line-gray bg-off-white shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="material-symbols-outlined text-viza-black" style={{ fontSize: '20px' }}>forum</span>
              <span className="text-sm font-semibold text-viza-black truncate">Viza Asistan</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={handleGoToFullChat}
                className="text-xs font-semibold text-ink hover:text-viza-black hover:underline underline-offset-2 px-2 py-1 rounded-lg cursor-pointer bg-transparent border-none"
              >
                Tam sohbete git
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Kapat"
                className="p-1.5 rounded-lg hover:bg-line-gray/40 text-neutral-gray bg-transparent border-none cursor-pointer"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
          </div>

          {!state.selectedCountry ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 px-6 text-center">
              <span className="material-symbols-outlined text-neutral-gray" style={{ fontSize: '32px' }}>public</span>
              <p className="text-sm text-ink">Sohbete başlamadan önce bir ülke seç.</p>
              <Link
                to="/main"
                title="Sohbete başlamak için önce bir ülke seçmelisiniz."
                className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-pear text-viza-black hover:brightness-95 transition-all"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>public</span>
                Ülke Seç
              </Link>
            </div>
          ) : (
            <>
              <div ref={messagesContainerRef} className="chat-messages flex-1 overflow-y-auto">
                {state.chatHistory.length === 0 && (
                  <>
                    <div className="message bot">
                      <div className="message-avatar bot-avatar">
                        <img src="/assets/logo/ai-profile.png" alt="AI Assistant" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                      </div>
                      <div className="message-content text-sm">
                        Merhaba! Vize süreciyle ilgili aklına takılan bir şey mi var?
                      </div>
                    </div>
                    {/* T4: same starter chips as ChatPage's welcome card (compact variant) —
                        prompt chips send through this widget's own handleSendMessage; the
                        passport 'action' chip has no local upload panel in the widget (that
                        lives on /chat, T5's file), so it just routes there. */}
                    <ChatStarterChips
                      chips={starterChips}
                      onSend={handleSendMessage}
                      onAction={() => handleGoToFullChat()}
                      variant="compact"
                    />
                  </>
                )}

                {state.chatHistory.map((msg, index) => (
                  <div key={msg.id ?? index} className={`message ${msg.sender}`}>
                    <div className={`message-avatar ${msg.sender}-avatar`}>
                      {msg.sender === 'user' ? (
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>person</span>
                      ) : (
                        <img src="/assets/logo/ai-profile.png" alt="AI Assistant" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                      )}
                    </div>
                    <div className="message-content text-sm">
                      {msg.isLoading || (msg.isStreaming && !msg.content) ? (
                        <div className="typing-indicator">
                          <div className="dot"></div>
                          <div className="dot"></div>
                          <div className="dot"></div>
                        </div>
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMessage(msg.content)) }} />
                      )}
                    </div>
                  </div>
                ))}

                {!isAdmin && !state.hasCompletedPayment && freeChatLimitReached && (
                  <div className="mx-1 mb-2 p-3 bg-white border border-line-gray rounded-xl">
                    <p className="text-xs text-ink mb-2">Ücretsiz mesaj hakkınız doldu. Sınırsız sohbet için Premium'a geçin.</p>
                    <button
                      type="button"
                      onClick={() => navigate('/payment')}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-pear text-viza-black hover:brightness-95 transition-all border-none cursor-pointer"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>diamond</span>
                      Premium'a Geç
                    </button>
                  </div>
                )}
              </div>

              <div className="chat-input-container shrink-0">
                <div className="input-group">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => {
                      if (e.target.value.length <= 1500) {
                        setMessage(e.target.value);
                        resizeTextarea();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (message.trim() && !isLoading) {
                          handleSendMessage();
                        }
                      }
                    }}
                    maxLength={1500}
                    placeholder={inputDisabled && !isLoading ? 'Mesaj hakkınız doldu...' : 'Bir soru sor...'}
                    disabled={inputDisabled}
                    rows={1}
                  />
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={inputDisabled || !message.trim()}
                    aria-label="Gönder"
                  >
                    <span className="material-symbols-outlined">send</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ChatWidget;
